import { useTranslation } from 'react-i18next';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';

interface IntegrationStatusBadgeProps {
  connected: boolean;
  /** OAuth landed but the merchant still has a step to complete before anything is live. */
  pending?: boolean;
  /** Still connected, but stopped until the user restarts it. */
  notRunning?: boolean;
  /** Replaces "Connected" where the integration proves more than a connection. */
  connectedLabel?: string;
}

/**
 * Standardized connection-status badge shown in every integration sheet header
 * next to the title: green when connected, neutral grey when not.
 */
export const IntegrationStatusBadge = ({
  connected,
  pending,
  notRunning,
  connectedLabel,
}: IntegrationStatusBadgeProps) => {
  const { t } = useTranslation('integrations');
  const awaitingAction = Boolean(pending) && !connected;
  const stopped = connected && Boolean(notRunning);

  return (
    <Badge
      variant={stopped ? 'error' : connected ? 'success' : awaitingAction ? 'warning' : 'secondary'}
      size="sm"
      className="px-2"
      data-testid="integration-status-badge"
      data-connected={connected || undefined}
      data-pending={awaitingAction || undefined}
      data-not-running={stopped || undefined}
    >
      {stopped
        ? t('status.not_running')
        : connected
          ? (connectedLabel ?? t('status.connected'))
          : awaitingAction
            ? t('status.finish_setup')
            : t('status.not_connected')}
    </Badge>
  );
};

export default IntegrationStatusBadge;
